import { useState } from 'preact/hooks';
import { api, mensagemDoErro, qs } from '../api';
import { dataCurta } from '../format';
import { useApi, useDebounce } from '../hooks';
import { useSecretarias } from '../lookups';
import { pode } from '../permissoes';
import { usuario } from '../state';
import type { Projeto } from '../types';
import { Campo, Carregando, ErroForm, Modal, Selo, avisar, confirmar } from '../ui';
import { useRecarga } from './comum';
import { CampoData } from '../componentes/Campos';

export function ProjetosPage() {
  const podeEscrever = pode(usuario.value?.perfil, 'projeto:write');
  const { lista: secretarias, porId } = useSecretarias();
  const [secretariaId, setSecretariaId] = useState('');
  const [ativo, setAtivo] = useState('');
  const [busca, setBusca] = useState('');
  const q = useDebounce(busca, 300);
  const [recarga, recarregar] = useRecarga();
  const projetos = useApi<Projeto[]>(`/projetos${qs({ secretariaId, ativo, q })}`, recarga);
  const [editando, setEditando] = useState<Projeto | 'novo' | null>(null);

  async function alternar(p: Projeto) {
    const acao = p.ativo ? 'Desativar' : 'Reativar';
    const ok = await confirmar(
      p.ativo ? `Desativar "${p.titulo}"? Ele deixa de aceitar novas ações, mas as ações existentes continuam.` : `Reativar "${p.titulo}"?`,
      { confirmar: acao },
    );
    if (!ok) return;
    try {
      await api(`/projetos/${p.id}`, { method: 'PATCH', body: { ativo: !p.ativo } });
      avisar(p.ativo ? 'Projeto desativado.' : 'Projeto reativado.');
      recarregar();
    } catch (e) {
      avisar(mensagemDoErro(e), 'erro');
    }
  }

  const lista = projetos.dados ?? [];

  return (
    <main class="pagina">
      <header class="pagina-topo">
        <div>
          <h1>Projetos</h1>
          <p class="dica">Cada ação pertence a um projeto, e cada projeto a uma secretaria.</p>
        </div>
        {podeEscrever && <button type="button" class="botao primario" onClick={() => setEditando('novo')}>+ Novo projeto</button>}
      </header>

      <section class="barra-filtros" aria-label="Filtros">
        <label class="campo"><span>Buscar</span><input type="search" value={busca} onInput={(e) => setBusca(e.currentTarget.value)} placeholder="Título do projeto…" /></label>
        {secretarias.length > 1 && (
          <label class="campo"><span>Secretaria</span>
            <select value={secretariaId} onChange={(e) => setSecretariaId(e.currentTarget.value)}>
              <option value="">Todas</option>
              {secretarias.map((s) => <option key={s.id} value={s.id}>{s.sigla}</option>)}
            </select>
          </label>
        )}
        <label class="campo"><span>Situação</span>
          <select value={ativo} onChange={(e) => setAtivo(e.currentTarget.value)}>
            <option value="">Todos</option><option value="true">Ativos</option><option value="false">Inativos</option>
          </select>
        </label>
      </section>

      {projetos.erro && <ErroForm texto={mensagemDoErro(projetos.erro)} />}
      {projetos.carregando && !projetos.dados && <Carregando />}

      <div class="tabela-rolagem">
        <table class="tabela">
          <caption class="so-leitor">Lista de projetos</caption>
          <thead><tr><th scope="col">Projeto</th><th scope="col">Secretaria</th><th scope="col">Período</th><th scope="col" class="num">Ações</th><th scope="col">Situação</th>{podeEscrever && <th scope="col"><span class="so-leitor">Ações</span></th>}</tr></thead>
          <tbody>
            {lista.map((p) => {
              const s = porId.get(p.secretariaId);
              return (
                <tr key={p.id} class={p.ativo ? '' : 'cancelada'}>
                  <td><strong>{p.titulo}</strong>{p.descricao && <small class="sub">{p.descricao.slice(0, 90)}{p.descricao.length > 90 ? '…' : ''}</small>}</td>
                  <td>{s ? <><i class="ponto" style={{ background: s.cor }} />{s.sigla}</> : '—'}</td>
                  <td>{p.inicio || p.fimPrevisto ? `${dataCurta(p.inicio, true)} → ${dataCurta(p.fimPrevisto, true)}` : '—'}</td>
                  <td class="num">{p.totalAcoes ?? 0}</td>
                  <td><Selo texto={p.ativo ? 'Ativo' : 'Inativo'} tom={p.ativo ? 'verde' : 'cinza'} /></td>
                  {podeEscrever && (
                    <td class="acoes-linha">
                      <button type="button" class="link" onClick={() => setEditando(p)}>Editar</button>
                      <button type="button" class="link" onClick={() => void alternar(p)}>{p.ativo ? 'Desativar' : 'Reativar'}</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!projetos.carregando && lista.length === 0 && !projetos.erro && (
          <div class="vazio-grande">
            <p>Nenhum projeto encontrado.</p>
            {podeEscrever && <button type="button" class="botao primario" onClick={() => setEditando('novo')}>Cadastrar projeto</button>}
          </div>
        )}
      </div>

      {editando && <ProjetoForm projeto={editando === 'novo' ? null : editando} onFechar={() => setEditando(null)} onSalvo={() => { setEditando(null); recarregar(); }} />}
    </main>
  );
}

function ProjetoForm({ projeto, onFechar, onSalvo }: { projeto: Projeto | null; onFechar: () => void; onSalvo: () => void }) {
  const { lista: secretarias } = useSecretarias();
  const [secretariaId, setSecretariaId] = useState(projeto?.secretariaId ?? '');
  const [titulo, setTitulo] = useState(projeto?.titulo ?? '');
  const [descricao, setDescricao] = useState(projeto?.descricao ?? '');
  const [inicio, setInicio] = useState(projeto?.inicio ?? '');
  const [fim, setFim] = useState(projeto?.fimPrevisto ?? '');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function salvar(e: Event) {
    e.preventDefault();
    setErro('');
    if (titulo.trim().length < 3) return setErro('O título precisa ter ao menos 3 caracteres.');
    if (inicio && fim && fim < inicio) return setErro('O fim previsto não pode ser anterior ao início.');
    setSalvando(true);
    try {
      const corpo = { titulo: titulo.trim(), descricao: descricao.trim() || null, inicio: inicio || null, fimPrevisto: fim || null };
      if (projeto) await api(`/projetos/${projeto.id}`, { method: 'PATCH', body: corpo });
      else await api('/projetos', { method: 'POST', body: { ...corpo, secretariaId } });
      avisar(projeto ? 'Projeto atualizado.' : 'Projeto cadastrado.');
      onSalvo();
    } catch (err) {
      setErro(mensagemDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal titulo={projeto ? 'Editar projeto' : 'Novo projeto'} onFechar={onFechar}>
      <form class="form" onSubmit={salvar}>
        <Campo rotulo="Secretaria *" dica={projeto ? 'A secretaria de um projeto não pode ser alterada.' : undefined}>
          {(id) => (
            <select id={id} required disabled={!!projeto} value={secretariaId} onChange={(e) => setSecretariaId(e.currentTarget.value)}>
              <option value="">Selecione…</option>
              {secretarias.filter((s) => s.ativa || s.id === secretariaId).map((s) => <option key={s.id} value={s.id}>{s.sigla} — {s.nome}</option>)}
            </select>
          )}
        </Campo>
        <Campo rotulo="Título *">{(id) => <input id={id} required minLength={3} maxLength={200} value={titulo} onInput={(e) => setTitulo(e.currentTarget.value)} />}</Campo>
        <Campo rotulo="Descrição">{(id) => <textarea id={id} rows={3} maxLength={5000} value={descricao} onInput={(e) => setDescricao(e.currentTarget.value)} />}</Campo>
        <div class="linha-form">
          <Campo rotulo="Início" larg="meio">{(id) => <CampoData id={id} value={inicio} onChange={setInicio} />}</Campo>
          <Campo rotulo="Fim previsto" larg="meio">{(id) => <CampoData id={id} value={fim} onChange={setFim} />}</Campo>
        </div>
        <ErroForm texto={erro} />
        <div class="modal-acoes">
          <button type="button" class="botao" onClick={onFechar}>Cancelar</button>
          <button type="submit" class="botao primario" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar'}</button>
        </div>
      </form>
    </Modal>
  );
}
